/* Shared by the real export (export/png.js) and the on-screen Flat-view
   preview (ui/chrome.js) — one geometry function so the two positions can
   never drift apart, even though they no longer share a drawing routine
   (see below).

   A single bottom-centred logo lockup, not a tiled repeat: this is the
   actual Illusio3D brand mark, sized and placed the same way any "made
   with…" credit sits under a deliverable.

   BUNDLED, NOT FETCHED, and that is a deliberate anti-tamper choice rather
   than a packaging preference. The mark used to load from the fixed public
   URL /illusio-watermark.png, and the drawing routine below skipped the mark
   if that image had not loaded — so blocking one predictable request (a
   devtools "Block request URL", an ad-blocker rule, a proxy) produced clean
   exports, for good, from anyone, with nothing about this app understood.
   Measured, not theorised: with that one request blocked, the stamp changed
   0 pixels of a frame it otherwise covers ~9,900 of. An aggressive filter
   list could also have done it to an honest customer by accident.

   `?inline` makes Vite emit the bytes as a data URI inside the bundle, so
   there is no separate request left to block. The file is greyscale+alpha
   (the artwork has no colour in it — every opaque pixel has r=g=b) at full
   2048px, which is what keeps the inlined copy to ~34 KB rather than the
   59 KB the RGBA original cost.

   src/assets/illusio-watermark.png is the copy that ships inside exports and
   is the one to change if the brand mark ever does. public/illusio-watermark.png
   is now only a public URL nothing in the app reads. */
import LOGO_SRC from '../assets/illusio-watermark.png?inline';

const logo = new Image();
logo.src = LOGO_SRC;
const logoReady = new Promise(resolve => {
  if(logo.complete && logo.naturalWidth) resolve();
  else { logo.onload = () => resolve(); logo.onerror = () => resolve(); }
});
/** Resolves once the image has settled (loaded or failed) — never rejects. */
export async function logoLoaded(){
  await logoReady;
  return !!logo.naturalWidth;
}

/* The same bytes, as something CSS can point at — the live preview sets a
   background-image rather than drawing to a canvas (see ui/chrome.js's note
   on why re-encoding per reposition was the zoom lag). A blob: URL rather
   than handing every element the data URI itself: a wall previews one mark
   per screen, and twelve copies of a 45 KB string in twelve style attributes
   is half a megabyte of DOM for one image the browser decodes once anyway.
   Local to the page, so there is nothing here to block either. */
function asObjectUrl(dataUri){
  try{
    const comma = dataUri.indexOf(',');
    if(comma < 0 || !/;base64/i.test(dataUri.slice(0, comma))) return dataUri;
    const bin = atob(dataUri.slice(comma + 1));
    const bytes = new Uint8Array(bin.length);
    for(let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([bytes], { type: 'image/png' }));
  }catch(_){
    /* No atob/Blob/URL, or a shape this did not expect: the data URI works in
       CSS on its own, it is only bulkier. */
    return dataUri;
  }
}
export const LOGO_URL = asObjectUrl(LOGO_SRC);

/* Sized off sqrt(W*H) — the frame's overall pixel size — rather than off W
   alone: a width-only fraction means the exact same logical mark reads
   huge on a wide landscape screen and tiny on a narrow portrait one, purely
   from the aspect ratio, with no change in actual resolution. sqrt(W*H) is
   the standard aspect-independent "size" of a W×H rectangle (its geometric
   mean — scales exactly like a linear dimension under uniform resizing,
   unlike area itself). SIZE_FRACTION is calibrated so a 9:16 frame (the
   reference size this was tuned to look right at) still gets the same
   width as the old W*0.63: solving sqrt(9k·16k)·f = 9k·0.63 for f gives
   12k·f = 5.67k, f = 0.4725, independent of k (any 9:16 resolution).
   Then made 20% smaller (0.4725 × 0.8) on request: it read too large. The
   margin is unchanged, so it still sits the same distance from the foot. */
const SIZE_FRACTION = 0.378;
const MARGIN_FRACTION = 0.004;   // same calibration, for the bottom margin
const FALLBACK_ASPECT = 334 / 2048;   // the real image's own h/w, used only in the gap before it's loaded

/** Where the logo sits within a W×H frame — pure geometry, no canvas
 *  involved, safe to call before logoLoaded() resolves (falls back to the
 *  real image's own aspect ratio, so a caller sizing something ahead of
 *  the first load still gets the right shape). */
export function watermarkRect(W, H, refW = W, refH = H){
  const aspect = logo.naturalWidth ? logo.naturalHeight / logo.naturalWidth : FALLBACK_ASPECT;
  /* Size from the REFERENCE frame, position in this one. They are the same
     frame for a single screen and differ for a corner: its two panels share a
     height and may not share a width, so sizing each off its own sqrt(W*H)
     gave the narrower panel a visibly smaller mark — two different-sized
     logos on one continuous piece, which reads as a mistake rather than as a
     mark. Both now take the first panel's size and sit centred at the foot of
     their own panel. */
  const size = Math.sqrt(refW * refH);
  const w = size * SIZE_FRACTION;
  const h = w * aspect;
  const marginBottom = Math.max(2, size * MARGIN_FRACTION);
  return { x: (W - w) / 2, y: H - h - marginBottom, w, h };
}

/* ── a mark that moves, for video ─────────────────────────────────────────
   A still gets one mark in one place and that is the end of it. A video does
   not: a fixed mark sits in the same pixels for the whole running time, which
   is the easy case for anything that removes one — crop it off, or patch a
   single rectangle across every frame at once. Moving it every few seconds
   means a would-be remover has to deal with each placement separately.

   Three rules follow from that, and the first is the one that matters most:

   1. The position is constant WITHIN a block. Drawing from Math.random() per
      frame would make the mark strobe around the screen thirty times a second
      — unwatchable, and worse at its job, since every frame would then be a
      different problem for the viewer rather than for the remover. So the
      position is a pure function of the block index.
   2. Block 0 is exactly where the still's mark sits. The first seconds of a
      video look like the PNG does, which is what makes the export recognisably
      the same mark rather than a different feature.
   3. Each block lands somewhere visibly away from the last one. Two
      consecutive draws can otherwise land almost on top of each other, and a
      jump that small reads as a rendering fault. */
const JUMP_SECONDS = 3;
/* How far a new placement must be from the previous one, as a fraction of the
   frame's shorter side. A few candidates are drawn until one clears it. */
const MIN_TRAVEL = 0.3;
const MAX_TRIES = 6;

/* A small integer hash: same block, same seed, same answer, every frame and
   every machine. Math.random() cannot be used at all here — see rule 1. */
function hashed(seed, block, k) {
  let h = Math.imul(block * 2654435761 + k * 40503 + 1, 0x9E3779B1) ^ Math.imul(seed | 0, 0x85EBCA6B);
  h ^= h >>> 15; h = Math.imul(h, 0x2C1B3C6D);
  h ^= h >>> 12; h = Math.imul(h, 0x297A2D39);
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

/* Walked from block 0 rather than jumped to, because rule 3 needs to know
   where the previous one landed. Cheap: a ten-second clip has three blocks,
   and the whole walk is arithmetic. Memoised per frame size and seed so it is
   not repeated for all nine hundred frames of an export. */
const walks = new Map();

function placements(W, H, refW, refH, seed, upTo) {
  const key = `${W}x${H}|${refW}x${refH}|${seed}`;
  let list = walks.get(key);
  if (!list) { list = [watermarkRect(W, H, refW, refH)]; walks.set(key, list); }
  /* Bounded, but not down to one entry: a video wall stamps every panel on
     every frame, and each panel size is its own key. Evicting all but the
     latest would throw the previous panel's walk away on every single call
     and rebuild it on the next. Cleared wholesale when it does get large,
     which only happens across exports. */
  if (walks.size > 64) { walks.clear(); walks.set(key, list); }

  const base = list[0];
  const margin = Math.max(2, Math.sqrt(refW * refH) * MARGIN_FRACTION);
  const spanX = Math.max(0, W - base.w - margin * 2);
  const spanY = Math.max(0, H - base.h - margin * 2);
  const need = MIN_TRAVEL * Math.min(W, H);

  while (list.length <= upTo) {
    const block = list.length;
    const prev = list[block - 1];
    let best = null, bestGap = -1;
    for (let t = 0; t < MAX_TRIES; t++) {
      const x = margin + hashed(seed, block, t * 2) * spanX;
      const y = margin + hashed(seed, block, t * 2 + 1) * spanY;
      const gap = Math.hypot(x - prev.x, y - prev.y);
      if (gap > bestGap) { bestGap = gap; best = { x, y }; }
      if (gap >= need) break;
    }
    /* The furthest of the candidates when none cleared the bar — on a frame
       too small to travel that far, "as far as this frame allows" is the
       honest answer rather than giving up and repeating the last spot. */
    list.push({ ...base, x: best.x, y: best.y });
  }
  return list;
}

/** Where the mark sits `seconds` into a video. Identical to watermarkRect()
 *  for the first {@link JUMP_SECONDS}, then a new place every block. */
export function watermarkRectAtTime(W, H, refW, refH, seconds, seed = 0) {
  const block = Math.max(0, Math.floor((seconds || 0) / JUMP_SECONDS));
  return placements(W, H, refW ?? W, refH ?? H, seed | 0, block)[block];
}

/** Draws the logo positioned within a full W×H canvas — for the real
 *  export (export/png.js), which composites straight onto the already-
 *  allocated final output canvas. NOT used by the live preview any more
 *  (ui/chrome.js) — that used to render into an intermediate canvas and
 *  hand the browser a data URL, but re-encoding the logo's own actual
 *  pixel content (detailed line art with an alpha channel, unlike a flat
 *  fill) to PNG on every reposition measured at 100–250ms regardless of
 *  how small that canvas was, which is exactly what "lags behind while
 *  zooming" was. Since the image's pixels never change between reposition
 *  — only where it sits — the live preview now points background-image
 *  straight at the source file and repositions it with plain CSS, which
 *  needs no encode step at all. */
/* THE MARK WITHOUT THE ARTWORK. A last resort, for the case where the bundled
   image somehow does not decode — a browser that refuses the data URI, a
   corrupted bundle, a canvas that will not take it.

   It exists because the alternative this replaced was `return`, i.e. ship the
   file clean, which turned any failure to load the logo into a free
   watermark-free export. A drawn wordmark is worse-looking than the artwork
   and completely fine: the mark's job is to say where the file came from, and
   text says it. Fitted to the same rectangle the image would have filled, so
   the geometry (and everything that positions against it) is unchanged. */
export function drawWordmark(c, r){
  const text = 'CREATED WITH ILLUSIO3D.COM';
  const font = s => `600 ${s}px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`;
  c.save();
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = '#fff';
  /* Measured at a nominal size and scaled, rather than guessed: the generic
     monospace family resolves to something different on every platform, so
     the only reliable width is the one this canvas reports. */
  const NOMINAL = 100;
  c.font = font(NOMINAL);
  const measured = c.measureText(text).width || NOMINAL * text.length * 0.6;
  c.font = font(Math.max(1, Math.min(NOMINAL * (r.w / measured), r.h)));
  c.fillText(text, r.x + r.w / 2, r.y + r.h / 2);
  c.restore();
}

export async function drawWatermarkPattern(c, W, H, refW, refH, motion){
  const haveLogo = await logoLoaded();
  /* `motion` is {seconds, seed} on a video frame and absent on a still. A
     still has one frame, so there is nothing for a moving mark to defend
     against and the fixed place is the right one. */
  const r = motion
    ? watermarkRectAtTime(W, H, refW ?? W, refH ?? H, motion.seconds, motion.seed)
    : watermarkRect(W, H, refW ?? W, refH ?? H);
  if(!haveLogo){ drawWordmark(c, r); return; }
  c.save();
  c.imageSmoothingEnabled = true;
  c.imageSmoothingQuality = 'high';
  c.drawImage(logo, r.x, r.y, r.w, r.h);
  c.restore();
}
